import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Chip } from '@material-ui/core';
import { useRouter } from 'next/router';
import { format } from 'date-fns';
import Image from 'next/image';
import Link from 'next/link';
import Avatar from '@material-ui/core/Avatar';
import clsx from 'clsx';

import {
  formatGigPrice,
  getBadgeIcon,
  getGigLabel,
  getGigOwner,
  getMilestoneDates,
} from 'utils/helpers';
import { closeModal, openModal } from 'features/utilsSlice';
import { deleteRequest } from 'utils/api';
import useCurrentProfile from 'hooks/useCurrentProfile';
import CloseIcon from 'public/icons/close-main.svg';
import NoAvatarImage from 'public/images/no-avatar.svg';
import ProfileRating from 'components/profile/ProfileRating';
import ConfirmationDialog from 'components/ConfirmationDialog';
import ProfileDialog from 'components/profile/ProfileDialog';
import ReadMore from 'components/ReadMore';
import FeedbackDialog from 'components/gigs/FeedbackDialog';
import styles from 'styles/components/gigs/Gigs.module.scss';

const size = { width: 30, height: 30 };
const left = { marginLeft: 'auto' };
const second = {
  order: 2,
  paddingLeft: 10,
  borderLeft: '1px solid #c8ccd9',
  alignSelf: 'flex-end',
};
const avatarFullSize = { width: '100%', height: '100%' };

const Gig = ({ gig, rating, setGigs, setCounts, isDialog, isOffer, handleClose }) => {
  const [leaveFeedback, setLeaveFeedback] = useState(true);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [openProfile, setOpenProfile] = useState(false);
  const router = useRouter();

  const user = useSelector(state => state.auth.currentUser);
  const isLoggedIn = useSelector(state => state.auth.loggedIn);
  const profile = useCurrentProfile();
  const dispatch = useDispatch();

  const hasLink = gig.description.includes('Link');

  const description = gig.guaranteed_gains ? gig.description.replace('1 ', '') : gig.description;

  const getLabelStyle = label => {
    if (label === 'This week') {
      return { color: '#ff4b4b' };
    } else if (label === 'Overdue') {
      return { color: '#ff4b4b', fontWeight: 'bold' };
    } else return {};
  };

  const getDeliveryTime = () => {
    const { interval, label } = getMilestoneDates(
      gig.next_delivery_start_time,
      gig.next_delivery_start_time
    );

    if (interval && gig.status !== 'completed') {
      return <span style={getLabelStyle(label)}>{label}</span>;
    } else if (gig.status === 'completed') {
      return (
        <span style={{ color: '#9f9ead' }}>
          Completed: {format(new Date(gig.completed_at), 'MM/dd/yy')}
        </span>
      );
    } else {
      return (
        <span style={{ color: '#9f9ead' }}>
          Created {format(new Date(gig.created_at), 'MM/dd/yy')}
        </span>
      );
    }
  };

  const href = gig.status === 'draft' ? `/gigs/${gig.id}/edit` : `/gigs/${gig.id}`;
  const gigOwner = getGigOwner(user, gig.user_id);

  const handleFeedbackSuccess = () => setLeaveFeedback(false);

  const shouldLeaveFeedback = useCallback(() => {
    if (gig.status !== 'completed' || !leaveFeedback) return false;

    const influencerAssigned = user.role === 'influencer' && gig.profile_id === profile?.id;
    const ownerCondition = !gig.reviews.some(
      r => r.reviewer_id === user.id && r.reviewer_type === 'User'
    );
    const influencerCondition = !gig.reviews.some(
      r => r.reviewer_id === profile?.id && r.reviewer_type === 'Profile'
    );

    if (gigOwner && influencerAssigned) {
      // This is agency buying from themselves
      return user.currently_managing_profile ? influencerCondition : ownerCondition;
    }

    if (gigOwner) return ownerCondition;

    if (influencerAssigned) return influencerCondition;

    return false;
  }, [
    gig.status,
    gig.profile_id,
    profile?.id,
    user.id,
    user.role,
    gig.reviews,
    leaveFeedback,
    gigOwner,
    user.currently_managing_profile,
  ]);

  const getMessage = () => {
    if (!isLoggedIn) return '';

    if (gig.status === 'completed') {
      if (shouldLeaveFeedback()) {
        return (
          <span
            className="color-main"
            onClick={e => {
              e.stopPropagation();
              dispatch(openModal({ name: 'Feedback', data: { gig } }));
            }}
          >
            Leave feedback
          </span>
        );
      } else {
        return <span className={styles.message}>Completed</span>;
      }
    }

    if (gig.status === 'pending') {
      if (gigOwner) {
        return <span className={clsx('color-main', styles.message)}>{getGigLabel(gig, user)}</span>;
      }

      return <span>Awaiting advertiser response</span>;
    }

    return <span className={styles.message}>{getGigLabel(gig, user)}</span>;
  };

  const removeDraft = () => {
    deleteRequest({
      endpoint: `gigs/${selectedDraftId}`,
    }).then(() => {
      dispatch(closeModal('Confirmation'));
      setGigs(prevState => prevState.filter(g => g.id !== selectedDraftId));
      setCounts(prevState => ({ ...prevState, draft: prevState.draft - 1 }));
    });
  };

  const handleOpenConfirmation = gigId => {
    dispatch(openModal({ name: 'Confirmation', data: { action: 'approve' } }));
    setOpenConfirmation(true);
    setSelectedDraftId(gigId);
  };

  const handleDialogClose = () => isDialog && handleClose();

  const price = gigOwner ? gig.budget : gig.amount;
  // For influencer buyers, we show the thumbnail of the milestone if it exists
  const thumbnail =
    gigOwner && user.role === 'influencer'
      ? gig.milestone_thumbnail_url || gig.thumbnail_url
      : gig.thumbnail_url;

  return (
    <div onClick={handleDialogClose}>
      <Link href={href} passHref>
        <div className={clsx(styles.card, { [styles.noHover]: isDialog })}>
          <div
            className={clsx(styles.imageContainer, {
              [styles.greyCover]: hasLink,
            })}
            style={
              thumbnail && !hasLink
                ? { backgroundColor: 'transparent' }
                : { backgroundColor: '#f2f4f9' }
            }
          >
            {hasLink ? (
              <Image src="/icons/bio-link.svg" width={50} height={50} alt="Bio Link" unoptimized />
            ) : (
              <Image
                src={thumbnail || '/icons/no-avatar-full.svg'}
                layout="fill"
                alt="Cover"
                unoptimized
              />
            )}

            {user.role === 'influencer' &&
              (gig.status === 'active' || gig.status === 'completed') &&
              router.pathname !== '/dashboard' && (
                <Chip
                  className="dark-chip"
                  label={gigOwner ? 'Bought' : 'Sold'}
                  color="secondary"
                />
              )}

            {isOffer && user.role === 'influencer' && !gigOwner && (
              <Chip
                className="dark-chip"
                label={gig.public ? 'Public offer' : 'Funded order'}
                color="secondary"
              />
            )}
          </div>

          <div className={styles.content}>
            <div className={styles.profileInfo}>
              {gigOwner && gig.profile_id ? (
                <div
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={e => {
                    e.stopPropagation();
                    setOpenProfile(true);
                    dispatch(
                      openModal({ name: 'Profile', data: { username: gig.profile.username } })
                    );
                  }}
                >
                  <Avatar style={size} src={gig.profile.avatar_url} alt="Profile">
                    <NoAvatarImage style={avatarFullSize} />
                  </Avatar>

                  <span className={styles.name}>{gig.profile.display_name}</span>
                </div>
              ) : (
                <>
                  <Avatar style={size} src={gig.user.avatar_url} alt="Profile">
                    <NoAvatarImage style={avatarFullSize} />
                  </Avatar>

                  <span className={styles.name}>{gig.user.display_name}</span>
                </>
              )}

              <span
                className={clsx(styles.date, { [styles.moveLeft]: gig.status === 'draft' })}
                style={gigOwner && !gig.profile_id ? left : {}}
              >
                {gig.status === 'pending' ? (
                  <Chip
                    icon={getBadgeIcon(gig.user.badge)}
                    label={gig.user.badge}
                    className="light-purple-chip"
                  />
                ) : (
                  getDeliveryTime()
                )}
              </span>
            </div>

            {/* {hasLink && (
              <div>
                {gig.milestones
                  .filter(m => m.url)
                  .map(milestone => (
                    <a
                      key={milestone.id}
                      target="_blank"
                      href={milestone.url}
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className={styles.hoverLink}
                    >
                      <h3>Biolink: {milestone.url}</h3>
                    </a>
                  ))}
              </div>
            )} */}

            <h3>{gig.title}</h3>

            <div style={{ marginBottom: 15 }}>
              <ReadMore description={description} mobileLength={100} desktopLength={100} />
            </div>

            <div className={styles.price}>
              <span style={rating ? second : {}}>{formatGigPrice(gig, price)}</span>

              {rating ? (
                <ProfileRating
                  rating={gigOwner ? gig.profile?.success_score : gig.user.success_score}
                />
              ) : (
                getMessage()
              )}
            </div>

            {gig.status === 'draft' && (
              <span
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOpenConfirmation(gig.id);
                }}
                style={{ position: 'absolute', right: 15, top: 16 }}
                className="hover-icon"
              >
                <CloseIcon />
              </span>
            )}
          </div>
        </div>
      </Link>

      {gig.status === 'completed' && shouldLeaveFeedback() && (
        <FeedbackDialog handleSuccess={handleFeedbackSuccess} />
      )}

      {openConfirmation && (
        <ConfirmationDialog
          title="Are you sure you want to remove this draft?"
          handleApprove={removeDraft}
        />
      )}

      {openProfile && <ProfileDialog />}
    </div>
  );
};

Gig.propTypes = {
  gig: PropTypes.object.isRequired,
  rating: PropTypes.bool,
  isOffer: PropTypes.bool,
  setGigs: PropTypes.func,
  setCounts: PropTypes.func,
  isDialog: PropTypes.bool,
};

Gig.defaultProps = {
  rating: false,
  setGigs: () => {},
  setCounts: () => {},
  isOffer: false,
  isDialog: false,
};

export default Gig;
